import { SAB_KINDS, type SabKind } from '../shared/constants.ts';
import { flag, withFlag } from '../shared/country.ts';
import type { ChatMsg, LbEntry, Leaderboards, VoteState } from '../shared/protocol.ts';
import { hydrateIcons, iconSvg, type IconName } from './icons.ts';

export interface UiHandlers {
  chat(text: string): void;
  vote(kind: SabKind): void;
  ready(): void;
  join(): void;
  mute(): void;
  leave(): void;
  /** Share / watch a recorded run (from the leaderboard, the chat, or your own last run). */
  share(rid: string, text: string): void;
  watch(rid: string): void;
  shareLast(): void;
  watchLast(): void;
}

export interface StatusView {
  icon: IconName;
  main: string;
  sub?: string;
  tone?: 'hot' | 'live';
  action?: { label: string; ghost?: boolean; run: () => void };
}

export interface OverlayView {
  title: string;
  score?: string;
  text?: string;
  action?: { label: string; icon?: IconName; pulse?: boolean; run: () => void };
  /** A second, quieter button next to the main one. */
  alt?: { label: string; icon: IconName; run: () => void };
}

/** Buttons floating at the bottom of the game screen (replay controls, sharing a run). */
export interface ScreenAction {
  label: string;
  icon: IconName;
  primary?: boolean;
  run: () => void;
}

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;

/** All DOM work lives here; main.ts decides *what* to show. Text only via textContent. */
export class Ui {
  readonly canvas = $<HTMLCanvasElement>('#game');
  readonly controlButtons = document.querySelectorAll<HTMLElement>('#controls [data-btn]');
  private readonly h: UiHandlers;
  private readonly chatLog = $<HTMLUListElement>('#chatLog');
  private readonly chatInput = $<HTMLInputElement>('#chatInput');
  private readonly status = $<HTMLDivElement>('#status');
  private readonly overlay = $<HTMLDivElement>('#overlay');
  private readonly toastEl = $<HTMLDivElement>('#toast');
  private readonly actions = $<HTMLDivElement>('#actions');
  private actionsKey = '';
  private readonly voteButtons = new Map<SabKind, HTMLButtonElement>();
  private readonly lbDialog = $<HTMLDialogElement>('#lb');
  private lb: Leaderboards = { day: [] };
  private lbTimer = 0;
  private myName = '';
  private toastTimer = 0;
  private statusKey = '';
  private overlayKey = '';

  constructor(h: UiHandlers) {
    this.h = h;
    hydrateIcons();
    $<HTMLFormElement>('#chatForm').addEventListener('submit', (e) => {
      e.preventDefault();
      const text = this.chatInput.value.trim();
      if (text) h.chat(text);
      this.chatInput.value = '';
    });
    for (const kind of SAB_KINDS) {
      const b = $<HTMLButtonElement>(`.vote-row [data-kind="${kind}"]`);
      b.addEventListener('click', () => {
        h.vote(kind);
        navigator.vibrate?.(15);
      });
      this.voteButtons.set(kind, b);
    }
    $('#muteBtn').addEventListener('click', () => h.mute());
    // M toggles sound on a keyboard (unless typing in the chat).
    window.addEventListener('keydown', (e) => {
      if ((e.key === 'm' || e.key === 'M') && !e.repeat && !e.ctrlKey && !e.metaKey && !e.altKey && document.activeElement !== this.chatInput) h.mute();
    });
    $('#lbBtn').addEventListener('click', () => {
      this.renderLb();
      this.lbDialog.showModal();
      this.lbTimer = window.setInterval(() => this.renderReset(), 1000);
    });
    this.lbDialog.addEventListener('close', () => clearInterval(this.lbTimer));
    this.lbDialog.querySelector('[data-close]')!.addEventListener('click', () => this.lbDialog.close());
    this.lbDialog.addEventListener('click', (e) => {
      if (e.target === this.lbDialog) this.lbDialog.close();
    });
    $('#lastRun').append(this.runButton('play', 'Watch your last run', () => h.watchLast()), this.runButton('share', 'Share your last run', () => h.shareLast()));
  }

  setMe(name: string, cc: string | undefined): void {
    this.myName = name;
    const b = $('#meName');
    b.textContent = withFlag(name, cc);
    b.style.color = nameColor(name);
  }

  /** Under your name: your latest recorded run, to watch or share (hidden until you have one). */
  setLastRun(r: { score?: number; dist?: number } | null): void {
    $('#lastRun').hidden = !r;
    if (!r) return;
    const t = $('#lastRun .lr-text');
    if (r.score === undefined) t.textContent = 'Last run';
    else t.replaceChildren(el('span', 'lr-label', 'Last run · '), el('b', '', `${r.score.toLocaleString()} pts`), el('span', 'lr-dist', ` · ${r.dist} m`));
  }

  setMuted(muted: boolean): void {
    const b = $<HTMLButtonElement>('#muteBtn');
    b.setAttribute('aria-pressed', String(muted));
    b.setAttribute('aria-label', muted ? 'Unmute sounds' : 'Mute sounds');
    b.title = muted ? 'Sound off (M)' : 'Sound on (M)';
    b.replaceChildren(iconSvg(muted ? 'muted' : 'sound'));
  }

  setStats(watchers: number, queue: number): void {
    $('#watchers b').textContent = watchers.toLocaleString();
    $('#qlen b').textContent = queue.toLocaleString();
  }

  setMode(playing: boolean): void {
    document.body.classList.toggle('playing', playing);
    $('#controls').hidden = !playing;
    $('#votes').hidden = playing;
    if (playing) this.chatInput.blur();
  }

  setStatus(s: StatusView): void {
    const key = JSON.stringify([s.icon, s.sub !== undefined, s.tone, s.action?.label]);
    if (key === this.statusKey) {
      this.status.querySelector('.main')!.textContent = s.main;
      const sub = this.status.querySelector('.sub');
      if (sub) sub.textContent = s.sub ?? '';
      return;
    }
    this.statusKey = key;
    this.status.className = s.tone ?? '';
    this.status.replaceChildren(iconSvg(s.icon));
    const txt = el('div', 'txt');
    txt.append(el('span', 'main', s.main));
    if (s.sub) txt.append(el('span', 'sub', s.sub));
    this.status.append(txt);
    if (s.action) {
      const b = el('button', s.action.ghost ? 'btn ghost' : 'btn', s.action.label);
      b.addEventListener('click', s.action.run);
      this.status.append(b);
    }
  }

  setOverlay(o: OverlayView | null): void {
    // Text-only changes (e.g. a ticking countdown) are patched in place: rebuilding
    // the button mid-tap would swallow the click.
    const key = o ? JSON.stringify([o.title, o.score, o.text !== undefined, o.action?.label, o.alt?.label]) : '';
    if (key === this.overlayKey) {
      const p = this.overlay.querySelector('p');
      if (p && o?.text !== undefined && p.textContent !== o.text) p.textContent = o.text;
      return;
    }
    this.overlayKey = key;
    this.overlay.hidden = !o;
    this.overlay.replaceChildren();
    if (!o) return;
    this.overlay.append(el('h2', '', o.title));
    if (o.score) this.overlay.append(el('div', 'score', o.score));
    if (o.text !== undefined) this.overlay.append(el('p', '', o.text));
    const row = el('div', 'buttons');
    if (o.action) {
      const b = el('button', o.action.pulse ? 'big pulse' : 'big', o.action.label);
      if (o.action.icon) b.prepend(iconSvg(o.action.icon));
      b.addEventListener('click', o.action.run);
      row.append(b);
    }
    if (o.alt) {
      const b = el('button', 'big alt', o.alt.label);
      b.prepend(iconSvg(o.alt.icon));
      b.addEventListener('click', o.alt.run);
      row.append(b);
    }
    if (row.childElementCount) this.overlay.append(row);
  }

  setActions(list: ScreenAction[] | null): void {
    const key = list ? JSON.stringify(list.map((a) => [a.label, a.icon, a.primary])) : '';
    if (key === this.actionsKey) return; // (same buttons: keep them, a rebuild mid-tap swallows the click)
    this.actionsKey = key;
    this.actions.hidden = !list;
    this.actions.replaceChildren();
    for (const a of list ?? []) {
      const b = el('button', a.primary ? 'primary' : '');
      b.setAttribute('aria-label', a.label);
      b.append(iconSvg(a.icon), el('span', '', a.label));
      b.addEventListener('click', a.run);
      this.actions.append(b);
    }
  }

  toast(text: string): void {
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (this.toastEl.hidden = true), 2500);
  }

  setChat(msgs: ChatMsg[]): void {
    this.chatLog.replaceChildren();
    for (const m of msgs) this.addChat(m);
  }

  addChat(m: ChatMsg): void {
    const log = this.chatLog;
    const stick = log.scrollTop + log.clientHeight >= log.scrollHeight - 30;
    // Every line: [flag or icon] [name + text, wrapping under itself] [share, for a finished run].
    const li = document.createElement('li');
    const side = el('span', 'side');
    const body = el('span', 'body');
    li.append(side, body);
    if (m.sys) {
      li.className = `sys ${m.k ?? ''}`;
      side.append(iconSvg(m.k === 'up' ? 'play' : m.k === 'out' ? 'skull' : 'hourglass'));
      body.append(el('b', '', withFlag(m.n, m.cc)), ` ${m.m}`);
      if (m.rid) {
        const rid = m.rid;
        li.append(this.runButton('share', 'Share this run', () => this.h.share(rid, `${withFlag(m.n, m.cc)} ${m.m.split(' · ')[0]} on GLOPE`)));
      }
    } else if (m.bot) {
      // A visible bot: a robot for a flag, a BOT tag, and no name colour.
      li.className = 'bot';
      side.textContent = '🤖';
      body.append(el('b', 'n', m.n), el('span', 'tag', 'BOT'), m.m);
    } else {
      side.textContent = flag(m.cc);
      const n = el('b', 'n', m.n);
      n.style.color = nameColor(m.n);
      body.append(n, m.m);
    }
    log.append(li);
    while (log.childElementCount > 120) log.firstElementChild!.remove();
    if (stick) log.scrollTop = log.scrollHeight;
  }

  setVotes(v: VoteState, cooldownLeft: number, enabled: boolean, mine: SabKind | null): void {
    for (const [kind, b] of this.voteButtons) {
      const n = v.v[kind] ?? 0;
      b.disabled = !enabled;
      b.classList.toggle('mine', mine === kind);
      b.querySelector('small')!.textContent = `${n}/${v.th}`;
      b.querySelector('em')!.style.width = `${Math.min(100, (n / v.th) * 100)}%`;
    }
    $('#voteInfo').textContent = !enabled ? '' : cooldownLeft > 0 ? `recharging ${Math.ceil(cooldownLeft / 1000)}s` : `${v.th} votes fire it`;
  }

  setLb(lb: Leaderboards): void {
    this.lb = lb;
    if (this.lbDialog.open) this.renderLb();
  }

  private renderLb(): void {
    const list = $('#lbList');
    list.replaceChildren();
    const rows = this.lb.day;
    if (!rows.length) list.append(el('li', 'empty', 'Nobody yet today. Be the first!'));
    for (const r of rows) {
      const me = r.name === this.myName;
      const li = el('li', me ? 'me' : '');
      li.append(el('span', 'who', withFlag(r.name, r.cc)), el('span', 'pts', r.score.toLocaleString()));
      const acts = el('span', 'acts');
      if (r.rid) acts.append(this.runButton('play', 'Watch this run', () => this.watch(r)), this.runButton('share', 'Share this run', () => this.shareEntry(r, me)));
      li.append(acts);
      list.append(li);
    }
    this.renderReset();
  }

  /** The board starts over at midnight UTC: when that is, in the viewer's own time. */
  private renderReset(): void {
    const now = Date.now();
    const at = now - (now % 86_400_000) + 86_400_000;
    const min = Math.ceil((at - now) / 60_000);
    const left = min >= 60 ? `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min` : `${min} min`;
    const clock = new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    $('#lbReset').textContent = `New board in ${left} (${clock} your time)`;
  }

  private watch(r: LbEntry): void {
    this.lbDialog.close();
    this.h.watch(r.rid!);
  }

  private shareEntry(r: LbEntry, me: boolean): void {
    const pts = `${r.score.toLocaleString('en')} pts`;
    this.h.share(r.rid!, me ? `I scored ${pts} on GLOPE today 💪` : `${withFlag(r.name, r.cc)} scored ${pts} on GLOPE today`);
  }

  private runButton(icon: IconName, label: string, run: () => void): HTMLButtonElement {
    const b = el('button', 'run-btn');
    b.type = 'button';
    b.title = label;
    b.setAttribute('aria-label', label);
    b.append(iconSvg(icon));
    b.addEventListener('click', run);
    return b;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function nameColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 75%, 68%)`;
}
